import {CommandBase} from "./commandBase";
import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {MapSectionKey} from "../utils/branchDiff";

// Writes (adds or overwrites) one entry in a key→value map section
// (localization / sounds / images / imagePacks), pulling Other's value into
// Current. `path` is the location within the section's slice of the model:
// single-level maps use [key]; localization uses [lang, key].
//
// `extraImages` lets an imagePacks migration carry the image-metadata rows the
// pack references but Current lacks, so the pack doesn't arrive pointing at
// missing images — all applied as one history entry.
export class SetMapEntryCommand extends CommandBase {
    sectionKey: MapSectionKey;
    path: string[];
    value: unknown;
    extraImages: { key: string; value: unknown }[];

    constructor(sectionKey: MapSectionKey, path: string[], value: unknown,
                extraImages: { key: string; value: unknown }[] = []) {
        super();
        this.sectionKey = sectionKey;
        this.path = path;
        this.value = value;
        this.extraImages = extraImages;
    }

    execute(data: RemoteConstantsModel): boolean {
        if (this.path.length === 0) return false;

        let parent: Record<string, unknown> = ensureSectionRoot(data, this.sectionKey);
        for (const seg of this.path.slice(0, -1)) {
            if (typeof parent[seg] !== 'object' || parent[seg] === null) parent[seg] = {};
            parent = parent[seg] as Record<string, unknown>;
        }
        const leaf = this.path[this.path.length - 1];

        let mutated = false;
        const cloned = JSON.parse(JSON.stringify(this.value));
        if (JSON.stringify(parent[leaf]) !== JSON.stringify(cloned)) {
            parent[leaf] = cloned;
            mutated = true;
        }

        if (this.extraImages.length > 0) {
            if (!data.images) data.images = {} as RemoteConstantsModel['images'];
            for (const row of this.extraImages) {
                if ((data.images as Record<string, unknown>)[row.key] === undefined) {
                    (data.images as Record<string, unknown>)[row.key] = JSON.parse(JSON.stringify(row.value));
                    mutated = true;
                }
            }
        }

        return mutated;
    }
}

function ensureSectionRoot(data: RemoteConstantsModel, sectionKey: MapSectionKey): Record<string, unknown> {
    switch (sectionKey) {
        case 'localization': if (!data.localization) data.localization = {}; return data.localization as Record<string, unknown>;
        case 'sounds':       if (!data.sounds) data.sounds = {} as RemoteConstantsModel['sounds']; return data.sounds as Record<string, unknown>;
        case 'images':       if (!data.images) data.images = {} as RemoteConstantsModel['images']; return data.images as Record<string, unknown>;
        case 'imagePacks':   if (!data.imagePacks) data.imagePacks = {}; return data.imagePacks as Record<string, unknown>;
        case 'constructionImagePacks': if (!data.constructionImagePacks) data.constructionImagePacks = {}; return data.constructionImagePacks as Record<string, unknown>;
    }
}
